import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { MagicWandIcon } from '@treams/treams-icons';
import {
	AiGeneratedGoalSuggestion,
	useAdvanceAiGeneratedGoalChatMutation,
	useStartAiGeneratedGoalChatMutation,
} from '@treams/treams-network';
import { AICard, Button, IconButton, Paragraph, Popover, tokens } from '@treams/treams-ui';

import { SuggestionsAIChatModal } from './SuggestionsAIChatModal';

const SubgoalLabel = styled(Paragraph)`
	display: flex;
	flex-direction: row;
	margin-bottom: ${tokens.gap.inline}px;

	&::before {
		--size: 18px;
		content: '';
		display: block;
		width: var(--size);
		height: var(--size);
		border-radius: ${tokens.radius.xs}px;
		border: 1px solid ${tokens.border.default};
		background: ${tokens.surface.white};
		box-sizing: border-box;
		margin-right: ${tokens.gap.element}px;
		flex-shrink: 0;
		margin-top: calc(12px - var(--size) / 2);
	}
`;

function GoalSuggestion(props: {
	data: AiGeneratedGoalSuggestion;
	onTake: (suggestion: AiGeneratedGoalSuggestion) => void;
}) {
	const { t } = useTranslation();

	return (
		<>
			<Box mb={2} maxWidth="80%">
				<AICard title={props.data.title}>
					<Paragraph mb={3}>{props.data.description}</Paragraph>
					{props.data.subgoals.map((subgoal, idx) => (
						<SubgoalLabel key={idx}>{subgoal}</SubgoalLabel>
					))}
				</AICard>
			</Box>
			<Flex>
				<Button
					onClick={async () => await props.onTake(props.data)}
					title={t('goals.ai.take')}
					type="ai"
				/>
			</Flex>
		</>
	);
}

export default function GoalSuggestionsAI(props: {
	onTakeSuggestion: (goal: AiGeneratedGoalSuggestion) => void;
	initialInput?: string;
}) {
	const { language } = useLocalize();
	const { t } = useTranslation();
	const [visible, setVisible] = useState(props.initialInput !== undefined);
	const [start, { loading: starting }] = useStartAiGeneratedGoalChatMutation();
	const [advance, { loading: advancing }] = useAdvanceAiGeneratedGoalChatMutation();

	const onTakeSuggestion = useCallback(
		async (suggestion) => {
			await props.onTakeSuggestion(suggestion);
			setVisible(false);
		},
		[setVisible],
	);

	return (
		<Popover.Target
			contents={
				<SuggestionsAIChatModal<AiGeneratedGoalSuggestion>
					translationKey="goals"
					initialInput={props.initialInput}
					onDismiss={() => setVisible(false)}
					startChat={async (reset) => {
						const response = await start({
							variables: {
								input: {
									locale: language.id,
									reset,
								},
							},
						});

						return { chat: response.data!.startAIGeneratedGoalChat.chat };
					}}
					starting={starting}
					advanceChat={async (chatID, userResponse) => {
						const response = await advance({
							variables: {
								input: {
									locale: language.id,
									chatID,
									userResponse,
								},
							},
						});

						return {
							chat: response.data!.advanceAIGeneratedGoalChat.chat,
							suggestion: response.data!.advanceAIGeneratedGoalChat.suggestion ?? null,
						};
					}}
					advancing={advancing}
					renderSuggestion={(suggestion) => (
						<GoalSuggestion data={suggestion} onTake={onTakeSuggestion} />
					)}
				/>
			}
			visible={visible}
			center
		>
			<IconButton
				type="ai"
				icon={<MagicWandIcon />}
				onClick={() => setVisible(true)}
				aria-label={t('goals.ai.shortcut.title')}
			/>
		</Popover.Target>
	);
}
